import {
  addActivityTab,
  socialLinksToIcons,
  stageTitle,
} from "../../contact-panel.js";
import {
  createEditor,
  createHiddenEditor,
} from "../../general/elements/tinymce.js";
import {
  addSpinner,
  getStartDate,
  getUser,
  removeSpinner,
  showSlowProviderResponseMessage,
  showToast,
  urlParams,
  uuidV4,
  wrappedGet,
  wrappedPost,
  yesNoConfirm,
} from "../../general/utils.js";
import { addWebsocketListener } from "../../general/websocket.js";
import { initTemplates } from "../campaigns/attachments.js";
import { disableSmsOption, updateInMailTooltip } from "./templates.js";

let templateMap;

const fillAiVariables = (campaignId, candidateMap) => {
  if (getUser().aiVariables) {
    wrappedPost({
      url: "/api/extension/fill_ai_variables",
      data: JSON.stringify({ campaignId, candidateMap }),
      success: () => {
        Object.keys(candidateMap).forEach((candidateId) => {
          templateMap[candidateId].forEach((template) => {
            template.aiVarsState = "pending";
          });
        });
      },
    });
  }
};

const campaignId = urlParams.get("campaignId");
let currentOutreachRow;

let USERS;
const getUserEmail = (id) => {
  const user = USERS.filter((obj) => obj.userId === id);
  return user[0]?.email;
};

const parser = new DOMParser();

const aiVarsInTemplate = (template) => {
  const miniDom = parser.parseFromString(template.messageText, "text/html");
  const aiVarSpans = miniDom.getElementsByClassName("ai-var");
  return aiVarSpans.length > 0;
};

const selectAiVarsInState = (candidateId, state, negate = false) =>
  templateMap[candidateId].filter(aiVarsInTemplate).filter((template) => {
    /*
      Pass in `negate`=true to check if any AI variable for the candidate's templates is NOT in `state`.
      Valid values for state are: undefined, 'pending', 'filled'.
    */
    const templateInState = template.aiVarsState === state;
    // templateInState=true, negate=true => false
    // templateInState=true, negate=false => true
    // templateInState=false, negate=true => true
    // templateInState=false, negate=false => false
    return templateInState !== negate;
  });

const getUnmappedVariables = (selected = false) => {
  const ret = [];
  const table = $("#outreachTable").DataTable();
  const indexes = table.rows({ selected }).indexes();

  indexes.toArray().forEach((index) => {
    const row = table.row(index).data();
    Object.values(templateMap[row.candidateId]).forEach((template) => {
      if (template.deleted) return;
      ["subject", "messageText"].forEach((key) => {
        const hasSubject =
          ["email", "inmail"].includes(template.mailType) &&
          !template.sendAsReply;
        if (key === "subject" && !hasSubject) return;

        const matches = template[key].match(/{{(.*)}}/gm);
        if (!matches) return;
        matches.forEach((match) => {
          const isUnique = !ret.some(
            (existingItem) =>
              existingItem.name === row.name &&
              existingItem.index === index &&
              existingItem.templateName === template.templateName &&
              existingItem.match === match,
          );
          if (isUnique) {
            ret.push({
              name: row.name,
              index,
              templateName: template.templateName,
              match,
            });
          }
        });
      });
    });
  });
  return ret;
};

export const markUnmappedVariables = () => {
  const table = $("#outreachTable").DataTable();
  const unmappedVars = getUnmappedVariables();
  const indexes = table.rows().indexes();

  indexes.toArray().forEach((index) => {
    const row = table.row(index).data();
    row.unmappedVariables = unmappedVars.some((v) => v.index === index);
  });
  table.rows().invalidate("data").draw(false);
};

const TEMPLATE_DICT = {};

const createTemplateItem = ({ template, templates, isFirst, isLast }) => {
  const chevron = isLast
    ? ""
    : '<div class="text-center m-3"><i class="fas fa-chevron-down"></i></div>';
  const id = uuidV4();

  template.id = id;

  const originalTemplate = JSON.parse(JSON.stringify(template));

  const options = [
    {
      value: "default",
      text:
        !isFirst && template.waitDays
          ? `${template.waitDays} days later`
          : originalTemplate.sendDatetime,
      selected:
        template.dontSendBefore === originalTemplate.dontSendBefore &&
        template.sendAfter === originalTemplate.sendAfter,
    },
  ];

  if (
    isFirst &&
    (originalTemplate.dontSendBefore || originalTemplate.sendAfter)
  ) {
    options.push({
      value: "sendImmediately",
      text: "Send Immediately",
      selected: !template.dontSendBefore && !template.sendAfter,
    });
  }
  options.push({
    value: "openDateModal",
    text: "Not before...",
    id: "openDateModal",
  });

  const optionsHtml = options
    .map((option) => [
      option.text,
      option.value,
      option.id ? `id="${option.id}"` : "",
      option.selected ? "selected" : "",
    ])
    .map(
      ([text, value, idStr, selectedStr]) =>
        `<option ${idStr} value="${value}" ${selectedStr}>${text}</option>`,
    )
    .join("\n");

  const html = `
        <div id="${template.templateId}">
          <div class="text-center template-heading p-1 m-0" style="font-weight: bold;">
            <div class="d-flex justify-content-center align-items-center">
              <span class="mr-1" style="font-size: 13px;">${template.templateName}</span>
              <div id="dontSendBeforeDateSelect" class="dropdown d-flex round-right ml-1 bg-white">
                <select id="${id}-selectDatePicker" class="selectpicker">
                  ${optionsHtml}
                </select>
              </div>
              <i class="ml-2 fas fa-info-circle" data-toggle="tooltip" data-bss-tooltip="" title="Please note that any send restrictions set on the campaign itself will overwrite this setting."
              ></i>
            </div>
            <br>
            <small style="font-size: 10.5px;">From ${template.senderUserEmail}</small>
          </div>
          <div class="mailtype-wrapper d-flex align-items-center justify-content-between no-shadow">
            <select class="selectpicker mailType p-2" id="${id}-mailType">
              <option data-content="<i class='text-info fas fa-envelope fa-fw mr-2'></i>Email" value="email"></option>
              <option class="mailType_sms" data-content="<i class='text-success fas fa-sms fa-fw mr-2'></i>SMS" value="sms"></option>
              <option data-content="<i class='text-warning fas fa-phone fa-fw mr-2'></i>Phone Call" value="phoneCall"></option>
              <option class="mailType_inmail" data-content="<i class='text-primary fab fa-linkedin fa-fw mr-2'></i>InMail" value="inmail"></option>
              <option data-content="<i class='text-success fab fa-linkedin-in fa-fw mr-2'></i>Connection Request" value="linkedinConnectionRequest"></option>
              <option data-content="<i class='text-danger fas fa-clipboard-check fa-fw mr-2'></i>General Task" value="generalTask"></option>
            </select>
            <div class="float-right p-0">
              <button id="${id}-remove" data-id="${id}" class="remove-step-btn btn border-0 text-danger mr-2" style="height: 100%; font-size: 13px;">Remove</button>
            </div>
          </div>
          <div data-toggle="tooltip" id="${id}-subjectRow" class="subjectRow input-group subject-input-group align-items-center justify-content-between" style="border-top: 0.15rem solid #E4E6EC">
            <span class="pl-3 pt-3 pb-3 pr-2 font-weight-bold">Subject</span>
            <div id="${id}-subjectEditor" class="message-subject-editor" style="flex:1;margin-top:0.1rem" /></div>
          </div>
          <div id="${id}-messageTextEditor"></div>
          <div class="row my-3">
            <div id="${id}-attachmentsCol" class="attachments-col col">
              <h6 class="attachments-title">Attachments</h6>
              <div class="attachments-container"></div>
            </div>
          </div>
          ${chevron}
        </div>
        `;

  $("#templateArea .card-body").append(html);
  $(`#${id}-selectDatePicker`).selectpicker("refresh");

  const revertTemplate = () => {
    if (originalTemplate.notBefore) {
      template.notBefore = originalTemplate.notBefore;
    } else {
      delete template.notBefore;
    }

    if (originalTemplate.waitDays) {
      template.waitDays = originalTemplate.waitDays;
      template.sendDatetime = originalTemplate.sendDatetime;
      delete template.dontSendBefore;
      delete template.dontSendBeforeDate;
    } else {
      template.sendDatetime = originalTemplate.sendDatetime;

      template.dontSendBefore = originalTemplate.dontSendBefore;
      template.dontSendBeforeDate = originalTemplate.dontSendBeforeDate;

      template.sendAfter = originalTemplate.sendAfter;
      if (originalTemplate.sendAfterDelay) {
        template.sendAfterDelay = { ...originalTemplate.sendAfterDelay };
      }
    }
  };

  $(`#${id}-selectDatePicker`).change((e) => {
    const { value } = e.target;
    if (value === "default") revertTemplate();
    else if (value === "sendImmediately") {
      delete template.notBefore;
      delete template.sendAfter;
      template.sendDatetime = "Send Immediately";
    } else if (value === "openDateModal") {
      $("#dontSendBeforeModal").modal("show");
      const minDate = $(e).hasClass("any-date") ? "" : new Date();

      $("#dontSendBeforeDate input").daterangepicker({
        autoApply: true,
        singleDatePicker: true,
        startDate: new Date(),
        minDate,
        locale: { format: "DD MMM YYYY" },
      });

      $("#submitSendBeforeDate")
        .off()
        .click(() => {
          const startDate = getStartDate("#dontSendBeforeDate input");
          const formattedDate = `Not before ${moment(startDate).format(
            "ddd DD MMM",
          )}`;
          template.dontSendBefore = true;
          template.dontSendBeforeDate = startDate;
          template.sendDatetime = formattedDate;
          template.notBefore = moment(startDate).unix();
          delete template.waitDays;
          $(`#${id}-selectDatePicker`).selectpicker("val", null);
          $(
            `[data-id="${id}-selectDatePicker"] .filter-option-inner-inner`,
          ).text(formattedDate);
          $("#dontSendBeforeModal").modal("hide");
        });
    }
  });

  $(`#${id}-mailType`).selectpicker("val", template.mailType);
  $(`#${id}-mailType`).selectpicker("refresh");

  $(`#${id}-subjectRow`).toggle(
    ["email", "inmail"].includes(template.mailType),
  );

  $(`#${id}-mailType`).change((e) => {
    template.mailType = e.target.value;
    $(`#${id}-subjectRow`).toggle(
      ["email", "inmail"].includes(template.mailType),
    );
  });

  const onChangeCallback = () => {
    markUnmappedVariables();
    template.messageText = tinymce.get(`${id}-messageTextEditor`).getContent();
  };

  createEditor({
    selector: `#${id}-messageTextEditor`,
    content: template.messageText,
    options: {
      onChangeCallback,
      aiVariablesTemplateId: getUser().aiVariables ? id : null,
      content_css: "/assets/css/theme/tinymce.min.css",
      templateId: template.templateId,
    },
  });

  createHiddenEditor(`#${id}-subjectEditor`, template.subject, {
    keyupCallback: (editor) => {
      const content = editor.getContent();

      template.subject = content;
      if (isFirst) {
        templates.slice(1, templates.length).forEach((temp) => {
          if (temp.sendAsReply) {
            temp.subject = `Re: ${content}`;
            tinymce.get(`${temp.id}-subjectEditor`).setContent(temp.subject);
          }
        });
      }
    },
    noHtml: true,
  });

  TEMPLATE_DICT[id] = template;
};

const populateTemplates = () => {
  $("#templateArea .card-body").empty();

  const data = $("#outreachTable").DataTable().row(currentOutreachRow).data();
  if (!data) return;

  const allTemplates = templateMap[data.candidateId];
  if (!allTemplates) return;

  const templates = allTemplates.reduce((ret, template) => {
    if (!template.deleted) ret.push(template);
    return ret;
  }, []);

  const n = templates.length;
  if (n === 0) return;

  templates.forEach((template) => {
    template.senderUserEmail = getUserEmail(template.senderUserId);
  });

  templates.forEach((template, i) => {
    createTemplateItem({
      template,
      templates,
      isFirst: i === 0,
      isLast: i === templates.length - 1,
    });
  });
  if (!getUser().hasSms) disableSmsOption();
  updateInMailTooltip();

  templateMap[data.candidateId].forEach((template) => {
    initTemplates({
      type: "outreach",
      templateId: template.id,
      attachmentIds: template.attachmentIds,
      candidateTemplates: templateMap[data.candidateId],
    });
  });
};

let selectedCandidateId;

const updateDocWithAiVars = (
  doc,
  setSkeletonText = false,
  filledAiVariables = [],
) => {
  const aiVarSpans = doc.getElementsByClassName("ai-var");

  // eslint-disable-next-line no-restricted-syntax
  for (const aiVarSpan of aiVarSpans) {
    // present+correct in all modern browsers for about 5 years.

    if (setSkeletonText) {
      aiVarSpan.classList.add("skeleton-text");
    } else {
      aiVarSpan.classList.remove("skeleton-text");
    }

    const aiVariableId = aiVarSpan.getAttribute("data-id");
    const filledAiVar = filledAiVariables.find(
      (aiVar) => aiVar.aiVariableId === aiVariableId,
    );
    if (filledAiVar) {
      let contentEditable = "true";
      if (filledAiVar.content === "") {
        // tinymce.getContent strips empty spans, we need
        // -something- in the span to stop that happening.  this
        // renders as &nbsp; inside the html, so a non-breaking
        // space. Not ideal but better than a disappearing span.
        filledAiVar.content = "\xa0";
        contentEditable = "false";
      }
      aiVarSpan.textContent = filledAiVar.content;
      aiVarSpan.setAttribute("contenteditable", contentEditable);
      aiVarSpan.removeAttribute("style");
      aiVarSpan.removeAttribute("title");
    }
  }
};

// for tinymce button.

const handleRowSelect = (row) => {
  currentOutreachRow = row;

  const table = $("#outreachTable").DataTable();
  const total = table.data().count();
  const data = table.row(row).data();

  selectedCandidateId = data?.existingCandidateId || data?.candidateId;

  if (!data) return;

  const index = table.row(row, { order: "current" }).index();

  $("#candidatePanelHeading").text(data.name);

  table.rows().every((rowIdx, _, rowLoop) => {
    if (rowIdx === index) {
      $("#candidatePanelCounter").text(`(${rowLoop + 1}/${total})`);
    }
    return true;
  });

  $("#candidatePanelEmails").text(data.emails.slice(0, 1).join(", "));

  $("#candidatePanelLinks").html(socialLinksToIcons(data.socialLinks));

  $(".unmapped-variable")
    .tooltip("dispose")
    .tooltip({ title: "Unmapped Variables" });

  populateTemplates();

  const displayActivityTab = () => {
    $("#activitiesDiv")
      .empty()
      .append('<p class="text-center">No activities found</p>');
    $("#candidateStageWrapper").empty();
    $("#candidateStatus").removeClass("bg-success bg-danger");
    $("#activityLoadingDiv").show();

    const isFromContacts = urlParams.get("fromContacts");

    if (data.existingCandidateId || isFromContacts) {
      $("#activitiesDiv")
        .empty()
        .append(
          '<div id="activityLoadingDiv" class="spinner-grow spinner-grow-sm text-primary w-100 mt-4" role="status"><span class="sr-only">Loading...</span></div>',
        );

      wrappedGet({
        url: "/api/activities/fill_feed",
        data: { candidateId: selectedCandidateId },
        success: (res) => {
          if (
            !res.feed ||
            (selectedCandidateId !== data.existingCandidateId &&
              !isFromContacts)
          )
            return;
          $("#activityLoadingDiv").hide();
          res.feed.forEach(addActivityTab);
          $(".user-tooltip")
            .tooltip("dispose")
            .tooltip({ html: true, target: "hover" });
        },
        error: (err) => {
          $("#activityLoadingDiv").hide();
          $("#activitiesDiv").append(
            '<p class="text-center">No activities found</p>',
          );
          if (err.status === 504) {
            showSlowProviderResponseMessage();
          }
        },
      });

      wrappedGet({
        url: "/api/candidates/get",
        data: { candidateId: selectedCandidateId },
        success: (res) => {
          if (selectedCandidateId !== data.existingCandidateId) return;
          const candidateStatusClass =
            res.status === "active" ? "bg-success" : "bg-danger";
          $("#candidateStatus").addClass(candidateStatusClass).text(res.status);
          $("#candidateStageWrapper").append(
            stageTitle(res.stage, { html: true }),
          );
        },
      });
    }
  };

  displayActivityTab();
};

const displayRowNeighbor = (inc) => {
  const outreachTable = $("#outreachTable").DataTable();
  const newRowId = outreachTable.row(currentOutreachRow).index() + inc;
  if (newRowId >= 0 && newRowId < outreachTable.rows().count()) {
    handleRowSelect(newRowId);
  }
};

const setSendBtnVisibilities = () => {
  const outreachTable = $("#outreachTable").DataTable();
  const selected = outreachTable.rows({ selected: true });
  const nSelected = selected.data().toArray().length;
  $("#sendSelectedOutreachButton").toggle(nSelected > 0);
  $("#sendOutreachButton").toggle(nSelected === 0);
};

const createOutreachTable = (res) => {
  templateMap = res.outreach.templateMap;
  const candidateMap = {};
  res.outreach.candidates.forEach((candidate) => {
    candidateMap[candidate.candidateId] = {
      campaigns: {
        [candidate.campaignId]: res.outreach.templateMap[candidate.candidateId],
      },
    };
  });

  Object.entries(templateMap).forEach(([, templates]) => {
    templates.forEach((template) => {
      const miniDom = parser.parseFromString(template.messageText, "text/html");
      updateDocWithAiVars(miniDom, true);
      template.messageText = miniDom.body.innerHTML;
    });
  });

  fillAiVariables(campaignId, candidateMap);

  const columns = res.customVars.map((x) => ({
    data: x.key,
    title: x.title,
    name: x.key,
    visible: ["firstName", "lastName", "company", "role"].includes(x.key),
  }));

  res.outreach.candidates.forEach((c) => {
    res.customVars.forEach((item) => {
      c[item.key] = c[item.key] || "";
    });
    c.emails = c.emails || [];
  });

  columns.unshift(
    {
      data: "candidateId",
      orderable: false,
      className: "select-checkbox",
      render: () => "",
    },
    {
      data: "unmappedVariables",
      searchable: false,
      orderable: true,
      render: (datum) =>
        datum
          ? '<i class="fas fa-exclamation unmapped-variable mx-3" style="color: red;"></i>'
          : "",
    },
    {
      data: "candidateId",
      searchable: false,
      orderable: false,
      className: "datatables-row-number-col",
    },
  );

  const fields = columns.reduce((ret, col) => {
    if (col.title) ret.push({ label: col.title, name: col.data });
    return ret;
  }, []);

  const editor = new $.fn.dataTable.Editor({
    idSrc: "candidateId",
    table: "#outreachTable",
    fields,
  });

  editor.on("edit", (_, __, candidate) => {
    const templates = templateMap[candidate.candidateId];

    $.post({
      url: "/api/spreadsheet/modify_templates",
      data: JSON.stringify({ candidate, templates }),
      success: (result) => {
        templateMap[candidate.candidateId] = result.templates;
        if (currentOutreachRow) {
          const currentRow = $("#outreachTable")
            .DataTable()
            .row(currentOutreachRow);
          if (currentRow.data().candidateId !== result.candidate.candidateId)
            return;
        }
        populateTemplates();
        markUnmappedVariables();
      },
    });
  });

  editor.on("remove ", (_, __, data) => {
    data.forEach((candidateId) => {
      delete templateMap[candidateId];
      if (selectedCandidateId === candidateId) handleRowSelect(0);
      setSendBtnVisibilities();
    });
  });

  const attachInlineEditor = (e) => {
    if ($(e.target).prop("tagName") === "TD") {
      editor.inline($(e.target), { onBlur: "submit" });
      handleRowSelect($(e.target).closest("tr"));
    }
  };

  $("#outreachTable").on(
    "click",
    "tbody td:not(:first-child)",
    attachInlineEditor,
  );
  $("#outreachTable").on(
    "dblclick",
    "tbody td:not(:first-child)",
    attachInlineEditor,
  );

  $("#outreachTable")
    .DataTable({
      buttons: [
        {
          extend: "colvis",
          className: "btn-light",
          text: '<i class="fa-regular fa-eye pr-1"></i>',
          columns: columns.map((col) => `${col.name}:name`),
        },
        { extend: "edit", editor, className: "btn-light ml-2" },
        { extend: "remove", editor, className: "btn-light ml-2" },
      ],
      columns,
      columnDefs: [
        {
          targets: "_all",
          render: (datum) => {
            if (typeof datum === "string")
              return datum.split(",").join("<br/>");
            return Array.isArray(datum) ? datum.join("<br/>") : datum;
          },
        },
      ],
      data: res.outreach.candidates,
      deferRender: true,
      dom: `
          <'row my-3'
            <'col table-buttons d-flex align-items-center'f
              <'div ml-2'B>
            >
          >
          <'row'
            <'col-12't>
            <'col-12'r>
          >
          <'row d-flex align-items-center'
            <'col-4'i>
            <'col-4 text-center'l>
            <'col-4'p>
          >`,
      fnInfoCallback: (_, iStart, iEnd, __, iTotal) =>
        `${iStart}-${iEnd} of <b>${iTotal}</b>`,
      info: false,
      rowCallback: (row, data) =>
        $(row).attr("data-unmapped", data.unmappedVariables ? "1" : "0"),
      order: [[1, "desc"]],
      paging: false,
      initComplete: () => {
        $("#outreachTable").wrap(
          "<div id='outreachTableWrapper' style = 'overflow:scroll; width:100%; height:68vh; position:relative;' ></div > ",
        );

        if ($(this).width() > $(this).parent().width()) {
          $(this).css({
            display: "block",
            "overflow-x": "auto",
            "white-space": "nowrap",
          });
        }
        $(this).find("tbody").css("cursor", "pointer");
        $(".unmapped-variable")
          .tooltip("dispose")
          .tooltip({ title: "Unmapped Variables" });
      },
      language: {
        search: '<i class="fa-regular fa-search" aria-hidden="true"></i>',
        searchPlaceholder: "Search",
      },
      select: { style: "multi+shift", selector: "td:first-child" },
      stateSave: true,
      stateDuration: 60 * 60 * 24 * 3,
      stateLoadParams: (_, state) => {
        state.search.search = "";

        state.start = 0;

        state.columns.forEach((col) => {
          col.search.search = "";
        });
      },
    })
    .on("order.dt search.dt", () => {
      const filter = { search: "applied", order: "applied" };
      const cols = $("#outreachTable")
        .DataTable()
        .column(".datatables-row-number-col", filter);

      cols.nodes().each((cell, i) => {
        cell.innerHTML = i + 1;
      });
    });
  markUnmappedVariables();
};

const changeCandidateData = (key, val) => {
  const outreachTable = $("#outreachTable").DataTable();
  const templateId = $("#templateSelector").val();
  const data = outreachTable.row(currentOutreachRow).data();
  templateMap[data.candidateId][templateId][key] = val;
  outreachTable.row(currentOutreachRow).data(data);
  markUnmappedVariables();
};

const connectSendOutreachButton = () => {
  const getOutreachData = (candidates) => {
    const data = {
      candidates,
      templateMap,
      campaignId,
      fromContacts: urlParams.get("fromContacts"),
      addedManually: urlParams.get("addedManually"),
      exportedFromCrm: urlParams.get("exportedFromCrm"),
      outreachId: urlParams.get("outreachId"),
    };

    ["projectIds", "tagIds"].forEach((key) => {
      if (urlParams.get(key)) data[key] = urlParams.get(key).split(",");
    });

    if (urlParams.get("actionedFrom"))
      data.actionedFrom = urlParams.get("actionedFrom");

    return data;
  };

  const outreachTable = $("#outreachTable").DataTable();

  outreachTable.on("select deselect", setSendBtnVisibilities);

  const displayUnmappedVariablesBanner = (unmappedVars) => {
    const messages = unmappedVars.map(
      (v) =>
        `${v.name || `Contact ${v.index + 1}`}, ${
          v.templateName
        }: No data for ${v.match}`,
    );

    const msg = {
      title: `Action required: Your campaign contains variables with missing data`,
      message: `${messages.join("<br>")}
        <br><br>
        Action needed:
        <br><br>
        - Fill in the missing data for affected contacts in the 'Contacts' table.
        <br>
        - Or, delete the variables in the relevant steps for affected contacts.`,
    };

    bootbox.alert({
      title: msg.title,
      message: msg.message,
      className: "capped-height",
      closeButton: false,
    });
  };

  const describeRowsWithUnfilledAiVars = (selected = false) => {
    const table = $("#outreachTable").DataTable();
    const entries = table.rows({ selected }).data().toArray();
    const selectedCandidateIds = entries.map((entry) => entry.candidateId);

    // map of candidateIds to unfilledAiVars
    const unfilledAiVars = Object.keys(templateMap).reduce((m, k) => {
      if (selectedCandidateIds.includes(k)) {
        const v = selectAiVarsInState(k, "filled", true);
        if (v.length > 0) {
          m[k] = v;
        }
      }
      return m;
    }, {});

    if (Object.values(unfilledAiVars).some((unfilled) => unfilled.length > 0)) {
      const allTable = $("#outreachTable").DataTable();
      const allEntries = allTable.data().toArray();
      const candidateNames = Object.keys(unfilledAiVars).map((cid) => {
        const rowIndex = allEntries.findIndex(
          (entry) => entry.candidateId === cid,
        );
        const entry = allEntries[rowIndex];
        return `Row ${rowIndex + 1}: ${entry.name}`;
      });
      candidateNames.sort();
      return candidateNames;
    }
    return [];
  };

  const checkUnmapped = (checkSelectedOnly) => {
    const unmappedVars = getUnmappedVariables(checkSelectedOnly);
    if (unmappedVars.length > 0) {
      displayUnmappedVariablesBanner(unmappedVars);
      return true;
    }

    const rowsWithMissingAiVars =
      describeRowsWithUnfilledAiVars(checkSelectedOnly);
    if (rowsWithMissingAiVars.length > 0) {
      const title = "ChatGPT is currently experiencing difficulties";
      const subtitle = `Please wait a moment and click the 'Refresh AI Variables' button on the editor`;
      showToast({ title, subtitle, type: "danger", timeout: 30000 });
      return true;
    }
    return false;
  };

  const submitOutreach = (opts) => {
    const { data, isPartial } = opts;
    $("#LoadingDiv").removeClass("invisible");
    $(isPartial ? "#sendSelectedOutreachButton" : "#sendOutreachButton").prop(
      "disabled",
      true,
    );

    wrappedPost({
      url: "/api/outreach/add",
      data: JSON.stringify(data),
      success: () => {
        const nCandidates = isPartial ? data.candidates.length : 1;
        const s = nCandidates === 1 ? "" : "s";
        $("#LoadingDiv").addClass("invisible");
        showToast({
          title: `Outreach scheduled successfully ${
            isPartial ? `for ${nCandidates} contact${s}` : ""
          } `,
          type: "success",
          timeout: 30 * 60000,
        });
        if (isPartial) {
          $("#outreachTable")
            .DataTable()
            .rows({ selected: true })
            .remove()
            .draw();
        }
        setSendBtnVisibilities();
        tinymce.editors.forEach((editor) => editor.setDirty(false));
        setTimeout(() => {
          window.location = "/outbox";
        }, 10000);
      },
      error: () => {
        $("#LoadingDiv").addClass("invisible");
      },
    });
  };

  const handleRejectedCandidates = (rejected, callback) =>
    Object.values(rejected).flat().forEach(callback);

  const checkPhoneNumbers = (opts) => {
    const { data, isPartial } = opts;
    const candidatesToCheck = [];
    const rejected = { invalidRegion: [], noNumber: [] };

    data.candidates.forEach((c) => {
      const templates = templateMap[c.candidateId];

      if (!c.selectedPhone && c.phones && c.phones.length)
        [c.selectedPhone] = c.phones;

      if (templates?.some((x) => x.mailType === "sms" && !x.deleted))
        if (c.selectedPhone) candidatesToCheck.push(c);
        else rejected.noNumber.push(c);
    });

    addSpinner("#sendOutreachButton");
    addSpinner("#sendSelectedOutreachButton");
    wrappedPost({
      url: "/api/outreach/check_phones",
      data: JSON.stringify({
        phones: candidatesToCheck.map((x) => x.selectedPhone),
      }),
      success: (res) => {
        removeSpinner("#sendOutreachButton");
        removeSpinner("#sendSelectedOutreachButton");

        candidatesToCheck.forEach((candidate) => {
          const { smsPossible } = res.results[candidate.selectedPhone];
          if (!smsPossible || smsPossible === "None")
            rejected.invalidRegion.push(candidate);
        });

        const rejectedCandidates = Object.values(rejected).flat();

        if (rejectedCandidates.length > 0) {
          const users = (candidate) => candidate.map((x) => ` ${x.name}`);

          const invalidRegion = rejected.invalidRegion.length;
          const invalidRegions = invalidRegion === 1 ? "" : "s";
          const invalidRegionHidden = invalidRegion ? "" : "hidden";
          const noNumber = rejected.noNumber.length;
          const noNumbers = noNumber === 1 ? "" : "s";
          const noNumberHidden = noNumber ? "" : "hidden";

          const regionMapper = { GB: "the UK", CA: "Canada", US: "the US" };
          const { smsRegions } = getUser();
          const validRegions = smsRegions.map((r) => regionMapper[r]);
          const validRegionsCopy =
            validRegions.length > 1
              ? `${validRegions.slice(0, -1).join(", ")} &amp; ${validRegions.slice(-1)}`
              : validRegions[0] || "";

          bootbox.dialog({
            message: `
              <p>Your campaign contains an <b>SMS step</b> and you have contacts with unsuitable data.</p>
              <p class="${invalidRegionHidden}">SMS is limited to phone numbers within the ${validRegionsCopy}. You have <b>${invalidRegion}</b> contact${invalidRegions} with a number outside of this region.
                <span id="showUnsupportedContacts" class="toggleContacts toggleButton">Show contact${invalidRegions}</span>
                <span style="display:none" id="hideUnsupportedContacts" class="toggleContacts toggleButton">Hide contact${invalidRegions}</span>
              </p>
              <textarea readonly id="UnsupportedContactsTextArea" class="contactsTextArea">${users(
                rejected.invalidRegion,
              )}
              </textarea>
              <p class="${noNumberHidden}">You have <b>${noNumber}</b> contact${noNumbers} without a phone number.
                <span id="showNoNumberContacts" class="toggleContacts toggleButton">Show contact${noNumbers}</span>
                <span style="display:none" id="hideNoNumberContacts" class="toggleContacts toggleButton">Hide contact${noNumbers}</span>
              </p>
              <textarea readonly id="NoNumberContactsTextArea" class="contactsTextArea">${users(
                rejected.noNumber,
              )}</textarea>`,
            className: "rejectContactsModal",
            buttons: {
              cancel: { label: "Cancel", className: "btn-light" },
              removeCandidates: {
                label: "Remove Contacts",
                className: `btn-primary ml-2 remove-candidates
                  ${
                    rejectedCandidates.length === data.candidates.length
                      ? "hidden"
                      : ""
                  }`,
                callback: () => {
                  handleRejectedCandidates(rejected, (candidate) => {
                    delete data.templateMap[candidate.candidateId];
                    data.candidates = data.candidates.filter(
                      (x) => x.candidateId !== candidate.candidateId,
                    );
                  });
                  submitOutreach({ data, isPartial });
                },
              },
              removeSMS: {
                label: "Remove SMS Steps",
                className: "btn-primary ml-2 remove-sms",
                callback: () => {
                  handleRejectedCandidates(rejected, (candidate) => {
                    if (data.templateMap[candidate.candidateId])
                      data.templateMap[candidate.candidateId].forEach(
                        (template) => {
                          if (template.mailType === "sms")
                            template.deleted = true;
                        },
                      );
                  });
                  submitOutreach({ data, isPartial });
                },
              },
            },
          });
        } else submitOutreach({ data, isPartial });

        ["UnsupportedContacts", "NoNumberContacts"].forEach((section) => {
          $(`#hide${section}, #show${section}`).click(() => {
            $(`#hide${section}, #show${section}`).toggle();
            $(`#${section}TextArea`).toggle();
          });
        });

        $(".remove-sms")
          .tooltip("dispose")
          .tooltip({
            title:
              "Affected contacts will be added to the campaign but will skip all SMS steps",
            placement: "bottom",
          });

        $(".remove-candidates")
          .tooltip("dispose")
          .tooltip({
            title: "Affected contacts will not be added to the campaign",
            placement: "bottom",
          });
      },
      error: () => {
        removeSpinner("#sendOutreachButton");
        removeSpinner("#sendSelectedOutreachButton");
      },
    });
  };

  $("#sendOutreachButton").click(() => {
    if (checkUnmapped()) return;
    checkPhoneNumbers({
      data: getOutreachData($("#outreachTable").DataTable().data().toArray()),
      isPartial: false,
    });
  });

  $("#sendSelectedOutreachButton").click(() => {
    if (checkUnmapped(true)) return;
    const data = getOutreachData(
      outreachTable.rows({ selected: true }).data().toArray(),
    );
    data.partial = true;
    const selectedData = JSON.parse(JSON.stringify(data));
    Object.keys(selectedData.templateMap).forEach((key) => {
      if (
        !selectedData.candidates
          .map((candidate) => candidate.candidateId)
          .includes(key)
      ) {
        delete selectedData.templateMap[key];
      }
    });
    checkPhoneNumbers({ data: selectedData, isPartial: true });
  });
};

const getOutreach = () => {
  wrappedGet({
    url: "/api/outreach/get",
    data: { outreachId: urlParams.get("outreachId") },
    success: (res) => {
      const removedContactsMessage = Object.entries(res.removedCandidates)
        .map(([reason, candidates]) => {
          if (candidates.length === 0) return "";

          const names = candidates.map(
            (candidate) => candidate.name || "No name found",
          );
          const total = names.length;
          const contactOrContacts = total > 1 ? "contacts" : "contact";
          const wasOrWere = total > 1 ? "were" : "was";
          const spacing = total > 1 ? "<br><br>" : " ";

          let reasonMessage;
          if (reason === "unsubscribed")
            reasonMessage = "removed as they are in your unsubscribed list:";
          else if (reason === "activeInCampaign")
            reasonMessage = "removed as they are already active in a campaign:";
          else if (reason === "finished")
            reasonMessage =
              "removed as they have already finished all the steps in the selected campaign:";

          return `The following ${total > 1 ? `${total} ` : ""}${contactOrContacts} ${wasOrWere} ${reasonMessage}${spacing}<i>${names.join("<br>")}</i><br><br>`;
        })
        .join("")
        .trim();

      const removedIds = Object.values(res.removedCandidates)
        .flat()
        .map((c) => c.candidateId);

      const filteredCandidates = res.outreach.previousCampaignCandidates.filter(
        (candidate) => !removedIds.includes(candidate.candidateId),
      );

      const previousCampaignMessage =
        filteredCandidates?.length > 0
          ? `The following contact${
              filteredCandidates.length > 1 ? "s have" : " has"
            } previously started this campaign before:<br><br>
            ${filteredCandidates
              .map((candidate) => `<i>${candidate.name}</i>`)
              .join("<br>")}<br><br>
            Where possible, ${filteredCandidates.length > 1 ? "these" : "this"} contact${filteredCandidates.length > 1 ? "s" : ""} will resume the campaign from the last step they reached before being removed.`
          : "";

      const outreachMessage = res.outreach.message;

      const showAlert = (message, callback) => {
        bootbox.alert({
          title:
            message === previousCampaignMessage
              ? "Previous campaign contacts"
              : "Some contacts were modified",
          message,
          className: "capped-height",
          closeButton: false,
          callback,
        });
      };

      const messages = [
        outreachMessage,
        removedContactsMessage,
        previousCampaignMessage,
      ].filter(Boolean);

      const showAlerts = (i = 0) => {
        if (i < messages.length) {
          showAlert(messages[i], () => showAlerts(i + 1));
        }
      };

      showAlerts();

      createOutreachTable(res);
      connectSendOutreachButton();

      const onChangeCallback = (editor) => {
        changeCandidateData("messageText", editor.getContent());
      };
      createEditor("#emailEditor", "", { onChangeCallback }).then(() => {
        handleRowSelect(0);
      });
    },
  });
};

export const onClickRefreshAiVariables = (templateId) => {
  const template = templateMap[selectedCandidateId].find(
    (t) => t.id === templateId,
  );

  // restrict templates to one that was clicked.
  const candidateMap = {
    [selectedCandidateId]: { campaigns: { [campaignId]: [template] } },
  };
  const iframe = $(`#${templateId}-messageTextEditor_ifr`)[0];
  if (iframe && iframe.contentWindow) {
    const doc = iframe.contentWindow.document;
    updateDocWithAiVars(doc, true);
  }
  fillAiVariables(campaignId, candidateMap);
};

export const initializeOutreach = () => {
  $(document).on("click", ".remove-step-btn", (e) => {
    const id = e.target.getAttribute("data-id");
    const message = "Remove this step for this contact?";
    yesNoConfirm(message, () => {
      TEMPLATE_DICT[id].deleted = true;
      populateTemplates();
    });
  });

  addWebsocketListener((message) => {
    if (message.kind === "aiVariables") {
      if (!templateMap[message.candidateId]) return;
      const templateIndex = templateMap[message.candidateId].findIndex(
        (template) => message.templateId === template.templateId,
      );
      const miniDom = parser.parseFromString(
        templateMap[message.candidateId][templateIndex].messageText,
        "text/html",
      );

      const docs = [miniDom];

      if (message.candidateId === selectedCandidateId) {
        const { id } = templateMap[message.candidateId][templateIndex];
        const iframeDocument = $(`#${id}-messageTextEditor_ifr`)[0]
          .contentWindow.document;
        docs.push(iframeDocument);
      }

      docs.forEach((doc) =>
        updateDocWithAiVars(doc, false, message.filledAiVariables || []),
      );
      templateMap[message.candidateId][templateIndex].messageText =
        miniDom.body.innerHTML;
      templateMap[message.candidateId][templateIndex].aiVarsState = "filled";
    }
  });

  ["sourced", "sent", "replied", "interested"].forEach((stage) =>
    $(document).on("click", `.${stage}Dropdown`, () => {
      wrappedPost({
        url: "/api/candidates/modify",
        data: JSON.stringify({
          candidateIds: [selectedCandidateId],
          stage,
          campaignId,
        }),
        success: () => {
          $("#candidateStageWrapper")
            .empty()
            .append(stageTitle(stage, { html: true }));
        },
      });
    }),
  );

  $("#subjectInput").change((e) => {
    changeCandidateData("subject", e.target.value);
  });

  $("#previousCandidateBtn").click(() => displayRowNeighbor(-1));

  $("#nextCandidateBtn").click(() => displayRowNeighbor(1));

  wrappedGet({
    url: "/api/users/get_all",
    success: (res) => {
      USERS = res;
      getOutreach();
    },
  });

  $("#sendTestButton").click(() => {
    wrappedPost({
      url: "/api/campaigns/send_test",
      data: JSON.stringify({ campaignId }),
      success: () => {
        showToast({
          title: "Test email sent",
          subtitle:
            "Please check your inbox (Note: personalisations will not be included in the test email)",
          type: "success",
        });
      },
      error: (err) => {
        if (err.status === 504) showSlowProviderResponseMessage();
      },
      spinner: "#sendTestButton",
    });
  });

  $(document).on("click", () => {
    $(".tox-toolbar__overflow").hide();
  });
};
